import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    BarChart3,
    ChevronLeft,
    ChevronRight,
    Copy,
    CreditCard,
    FileText as FileIcon,
    History as HistoryIcon,
    Mail,
    Shield,
    Trash2,
    User,
} from 'lucide-react';
import { useCustomer, useCustomers, useDeleteCustomer } from '@/api/customers/use-customers.tsx';
import { usePermissions } from '@/hooks/usePermissions.ts';
import { useStartImpersonation } from '@/api/impersonation/use-impersonation.tsx';
import { useSnackbar } from 'notistack';
import DateTimeFormatter from '@/components/shared/DateTimeFormatter';

import InfoTab from './components/InfoTab';
import { ConditionalPhoneField } from '@/components/integrations/ConditionalPhoneField';
import { SendMailTrigger } from '@/components/ui/send-mail-trigger';
import CustomerTradingTab from './components/CustomerTradingTab';
import TransactionsTab from './components/TransactionsTab';
import DocumentsTab from './components/DocumentsTab';
import AuditLogTab from './components/AuditLogTab';
import KycTab from './components/KycTab';
import { Card, CardContent } from '@/components/ui/card.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Badge } from '@/components/ui/badge.tsx';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { PageContainer, PageHeader } from '@/components/shared/layout';
import DuplicateBadge from '@/components/leads/DuplicateBadge';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog.tsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs.tsx';
import { ModulePermission, PermissionAction } from '@/types/permissions.ts';
import { useSystemConfiguration } from '@/api/system-configuration/use-system-configuration';

const VALID_TABS = ['info', 'trading', 'transactions', 'documents', 'kyc', 'audit-log'] as const;

const CustomerDetailsSkeleton: React.FC = () => (
    <PageContainer>
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center gap-4">
                <Skeleton className="h-9 w-36 rounded-md" />
                <Skeleton className="h-8 w-48" />
            </div>
            <div className="flex items-center gap-3 ml-auto">
                <Skeleton className="h-9 w-24 rounded-md" />
                <Skeleton className="h-9 w-16 rounded-md" />
                <Skeleton className="h-9 w-24 rounded-md" />
                <Skeleton className="h-9 w-32 rounded-md" />
                <Skeleton className="h-9 w-20 rounded-md" />
            </div>
        </div>

        {/* Summary card */}
        <Card>
            <CardContent className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2">
                        <div className="flex items-start gap-4">
                            <Skeleton className="w-16 h-16 rounded-full flex-shrink-0" />
                            <div className="flex-1 space-y-3">
                                <div className="flex items-center gap-3">
                                    <Skeleton className="h-8 w-48" />
                                    <Skeleton className="h-6 w-28 rounded-full" />
                                </div>
                                <div className="flex items-center gap-4">
                                    <Skeleton className="h-5 w-52" />
                                    <Skeleton className="h-5 w-36" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <Skeleton className="h-8 w-24 rounded-md" />
                        <Skeleton className="h-8 w-24 rounded-md" />
                        <Skeleton className="h-8 w-32 rounded-md" />
                    </div>
                </div>
            </CardContent>
        </Card>

        {/* Tabs + content */}
        <Card>
            <div className="border-b bg-muted/30 p-2">
                <div className="flex gap-2">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <Skeleton key={i} className="h-9 flex-1 rounded-md" />
                    ))}
                </div>
            </div>
            <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} className="space-y-2">
                            <Skeleton className="h-4 w-24" />
                            <Skeleton
                                className="h-6"
                                style={{ width: `${55 + ((i * 11) % 35)}%` }}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </Card>
    </PageContainer>
);

const CustomerDetailsPage: React.FC = () => {
    const { customerId } = useParams<{ customerId: string }>();
    const { data: systemConfig } = useSystemConfiguration();
    const crmTimezone = systemConfig?.timezone ?? 'UTC';
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [tabValue, setTabValue] = useState(() => searchParams.get('tab') || 'info');

    // Track which tabs have ever been activated so we only mount them once clicked.
    const [loadedTabs, setLoadedTabs] = useState<Set<string>>(
        () => new Set([searchParams.get('tab') || 'info'])
    );

    const handleTabChange = (value: string) => {
        setLoadedTabs(prev => new Set([...prev, value]));
        setTabValue(value);
    };

    // Sync tab value with URL params
    useEffect(() => {
        if (tabValue && tabValue !== searchParams.get('tab')) {
            const newSearchParams = new URLSearchParams(searchParams);
            if (tabValue === 'info') {
                newSearchParams.delete('tab');
            } else {
                newSearchParams.set('tab', tabValue);
            }
            setSearchParams(newSearchParams, { replace: true });
        }
    }, [tabValue, searchParams, setSearchParams]);

    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isSettingsEditDialogOpen, setIsSettingsEditDialogOpen] = useState(false);
    const { hasPermission } = usePermissions();
    const { enqueueSnackbar } = useSnackbar();
    const startImpersonationMutation = useStartImpersonation();

    const {
        data: customerResponse,
        isLoading,
        refetch: refetchCustomer,
    } = useCustomer(customerId || '');
    const deleteCustomerMutation = useDeleteCustomer();

    const sortKey = searchParams.get('sort_key') || 'created_at';
    const sortDirection = (searchParams.get('sort_direction') === 'asc' ? 'asc' : 'desc') as
        | 'asc'
        | 'desc';

    const { data: allCustomersResponse } = useCustomers({
        per_page: 1000,
        sort_by: sortKey,
        sort_direction: sortDirection,
    });

    const customer = customerResponse?.data;
    const allCustomers = allCustomersResponse || [];

    const navigationInfo = useMemo(() => {
        if (!customerId || !allCustomers.length) {
            return { currentIndex: -1, hasPrevious: false, hasNext: false };
        }
        const currentIndex = allCustomers.findIndex((c: any) => c.id === customerId);
        if (currentIndex === -1) {
            return { currentIndex: -1, hasPrevious: false, hasNext: false };
        }
        return {
            currentIndex,
            hasPrevious: currentIndex > 0,
            hasNext: currentIndex < allCustomers.length - 1,
            previousCustomer: allCustomers[currentIndex - 1],
            nextCustomer: allCustomers[currentIndex + 1],
        };
    }, [customerId, allCustomers]);

    const handlePreviousCustomer = () => {
        if (navigationInfo.hasPrevious && navigationInfo.previousCustomer) {
            const queryString = searchParams.toString();
            navigate(
                queryString
                    ? `/customers/${navigationInfo.previousCustomer.id}?${queryString}`
                    : `/customers/${navigationInfo.previousCustomer.id}`
            );
        }
    };

    const handleNextCustomer = () => {
        if (navigationInfo.hasNext && navigationInfo.nextCustomer) {
            const queryString = searchParams.toString();
            navigate(
                queryString
                    ? `/customers/${navigationInfo.nextCustomer.id}?${queryString}`
                    : `/customers/${navigationInfo.nextCustomer.id}`
            );
        }
    };

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (
                event.target instanceof HTMLInputElement ||
                event.target instanceof HTMLTextAreaElement
            )
                return;
            if (event.key === 'ArrowLeft' && navigationInfo.hasPrevious) {
                event.preventDefault();
                handlePreviousCustomer();
            } else if (event.key === 'ArrowRight' && navigationInfo.hasNext) {
                event.preventDefault();
                handleNextCustomer();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [navigationInfo.hasPrevious, navigationInfo.hasNext]);

    /**
     * Back/breadcrumb target. Carries the list's filter and sort querystring so
     * returning from a customer lands on the same page of the same filtered list.
     */
    const customersListUrl = (() => {
        const queryString = searchParams.toString();
        return queryString ? `/customers?${queryString}` : '/customers';
    })();

    const handleDeleteConfirm = async () => {
        if (customerId) {
            await deleteCustomerMutation.mutateAsync(customerId);
            setIsDeleteDialogOpen(false);
            navigate('/customers');
        }
    };

    const handleImpersonateClick = async () => {
        if (!customerId) return;
        try {
            await startImpersonationMutation.mutateAsync({ customer_id: customerId });
            enqueueSnackbar(`Now impersonating ${customer?.name}`, { variant: 'success' });
            const currentHost = window.location.host;
            const traderHost = currentHost.replace('crm.', 'trader.');
            window.open(`${window.location.protocol}//${traderHost}`, '_blank');
        } catch (error: any) {
            enqueueSnackbar(
                error.response?.data?.message ||
                    error.response?.data?.error ||
                    error.message ||
                    'Failed to start impersonation',
                { variant: 'error' }
            );
        }
    };

    if (isLoading) {
        return <CustomerDetailsSkeleton />;
    }

    if (!customer) {
        return (
            <PageContainer>
                <PageHeader
                    title="Customer not found"
                    backTo={customersListUrl}
                    backLabel="Back to customers"
                />
            </PageContainer>
        );
    }

    const currentTab = VALID_TABS.includes(tabValue as any) ? tabValue : 'info';
    const rawStatus = customer.status as unknown;
    const statusLabel = customer.status?.label ?? (typeof rawStatus === 'string' ? rawStatus : '-');

    return (
        <PageContainer>
            <PageHeader
                title={customer.name}
                breadcrumbs={[
                    { label: 'Customers', to: customersListUrl },
                    { label: customer.name },
                ]}
                backTo={customersListUrl}
                backLabel="Back to customers"
                meta={
                    <div className="flex items-center gap-2">
                        <DuplicateBadge recordId={customer.id} />
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handlePreviousCustomer}
                            disabled={!navigationInfo.hasPrevious}
                            aria-label="Previous customer"
                        >
                            <ChevronLeft className="h-4 w-4" />
                            Previous
                        </Button>
                        {navigationInfo.currentIndex >= 0 && (
                            <div className="flex items-center gap-1 px-3 py-1 bg-muted rounded-md">
                                <span className="text-sm font-medium">
                                    {navigationInfo.currentIndex + 1}
                                </span>
                                <span className="text-sm text-muted-foreground">of</span>
                                <span className="text-sm font-medium">{allCustomers.length}</span>
                            </div>
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleNextCustomer}
                            disabled={!navigationInfo.hasNext}
                            aria-label="Next customer"
                        >
                            Next
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                }
                actions={
                    <>
                        {hasPermission(ModulePermission.CUSTOMERS, PermissionAction.EDIT) && (
                            <Button
                                variant="outline"
                                onClick={handleImpersonateClick}
                                disabled={startImpersonationMutation.isPending}
                            >
                                <User className="h-4 w-4" />
                                {startImpersonationMutation.isPending
                                    ? 'Starting...'
                                    : 'Login as Customer'}
                            </Button>
                        )}
                        {hasPermission(ModulePermission.CUSTOMERS, PermissionAction.DELETE) && (
                            <Button
                                variant="destructive"
                                onClick={() => setIsDeleteDialogOpen(true)}
                            >
                                <Trash2 className="h-4 w-4" />
                                Delete
                            </Button>
                        )}
                    </>
                }
            />

            {/* Customer Summary */}
            <Card>
                <CardContent className="p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2 space-y-4">
                            <div className="flex items-start gap-4">
                                <div className="flex-shrink-0">
                                    <div className="w-16 h-16 bg-primary/10 text-primary rounded-full flex items-center justify-center">
                                        <User className="h-8 w-8" />
                                    </div>
                                </div>
                                <div className="flex-1 space-y-2">
                                    <div className="flex items-center gap-3">
                                        {customer.customer_number && (
                                            <Badge
                                                variant="outline"
                                                className="font-mono text-primary border-primary/30"
                                            >
                                                #{customer.customer_number}
                                            </Badge>
                                        )}
                                        <StatusBadge
                                            status={statusLabel}
                                            kind="lead"
                                            color={customer.status?.color}
                                            label={statusLabel}
                                        />
                                    </div>
                                    <div className="flex items-center gap-4 text-muted-foreground">
                                        <div className="flex items-center gap-2">
                                            <Mail className="h-4 w-4" />
                                            <span>{customer.email}</span>
                                            <SendMailTrigger
                                                email={customer.email}
                                                recipientId={customer.id}
                                                recipientType="customer"
                                                recipientName={customer.name}
                                                className="flex-shrink-0"
                                            />
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 w-6 p-0"
                                                onClick={() =>
                                                    navigator.clipboard
                                                        .writeText(customer.email)
                                                        .then(() =>
                                                            enqueueSnackbar(
                                                                'Email copied to clipboard',
                                                                { variant: 'success' }
                                                            )
                                                        )
                                                }
                                                aria-label="Copy email to clipboard"
                                                title="Copy email to clipboard"
                                            >
                                                <Copy className="h-3 w-3" />
                                            </Button>
                                        </div>
                                        {customer.phone && (
                                            <div className="flex items-center gap-2">
                                                <ConditionalPhoneField
                                                    phoneNumber={customer.phone}
                                                    customerId={customer.id}
                                                    className="flex-1 min-w-0"
                                                />
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-6 w-6 p-0 flex-shrink-0"
                                                    onClick={() =>
                                                        navigator.clipboard
                                                            .writeText(customer.phone || '')
                                                            .then(() =>
                                                                enqueueSnackbar(
                                                                    'Phone copied to clipboard',
                                                                    { variant: 'success' }
                                                                )
                                                            )
                                                    }
                                                    aria-label="Copy phone to clipboard"
                                                    title="Copy phone to clipboard"
                                                >
                                                    <Copy className="h-3 w-3" />
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2 px-3 py-1 bg-muted rounded-md">
                                <span className="text-xs font-medium text-muted-foreground">
                                    <DateTimeFormatter
                                        date={customer.created_at}
                                        timeZone={crmTimezone}
                                    />
                                </span>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Tabs */}
            <Card className="border-border bg-card">
                <Tabs value={currentTab} onValueChange={handleTabChange} className="w-full">
                    <div className="border-b bg-muted/30">
                        <TabsList className="grid w-full grid-cols-3 sm:grid-cols-6">
                            <TabsTrigger value="info" className="flex items-center gap-2">
                                <User className="h-4 w-4" />
                                Info
                            </TabsTrigger>
                            <TabsTrigger value="trading" className="flex items-center gap-2">
                                <BarChart3 className="h-4 w-4" />
                                Trading
                            </TabsTrigger>
                            <TabsTrigger value="transactions" className="flex items-center gap-2">
                                <CreditCard className="h-4 w-4" />
                                Transactions
                            </TabsTrigger>
                            <TabsTrigger value="documents" className="flex items-center gap-2">
                                <FileIcon className="h-4 w-4" />
                                Documents
                            </TabsTrigger>
                            <TabsTrigger value="kyc" className="flex items-center gap-2">
                                <Shield className="h-4 w-4" />
                                KYC
                            </TabsTrigger>
                            <TabsTrigger value="audit-log" className="flex items-center gap-2">
                                <HistoryIcon className="h-4 w-4" />
                                Audit Log
                            </TabsTrigger>
                        </TabsList>
                    </div>

                    <TabsContent value="info" className="p-6 space-y-6">
                        {loadedTabs.has('info') && (
                            <InfoTab
                                customer={customer}
                                onSettingsEdit={() => setIsSettingsEditDialogOpen(true)}
                                refetchCustomer={refetchCustomer}
                            />
                        )}
                    </TabsContent>

                    <TabsContent value="trading" className="p-6 space-y-6">
                        {loadedTabs.has('trading') && (
                            <CustomerTradingTab customerId={customer.id} />
                        )}
                    </TabsContent>

                    <TabsContent value="transactions" className="p-6 space-y-6">
                        {loadedTabs.has('transactions') && (
                            <TransactionsTab customerId={customer.id} />
                        )}
                    </TabsContent>

                    <TabsContent value="documents" className="p-6 space-y-6">
                        {loadedTabs.has('documents') && <DocumentsTab customerId={customer.id} />}
                    </TabsContent>

                    <TabsContent value="kyc" className="p-6 space-y-6">
                        {loadedTabs.has('kyc') && (
                            <KycTab customerId={customer.id} customerName={customer.name} />
                        )}
                    </TabsContent>

                    <TabsContent value="audit-log" className="p-6 space-y-6">
                        {loadedTabs.has('audit-log') && <AuditLogTab customerId={customer.id} />}
                    </TabsContent>
                </Tabs>
            </Card>

            {/* Delete Dialog */}
            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Delete Customer</DialogTitle>
                    </DialogHeader>
                    <p className="text-muted-foreground">
                        Are you sure you want to delete this customer? This action cannot be undone.
                    </p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDeleteConfirm}
                            variant="destructive"
                            disabled={deleteCustomerMutation.isPending}
                        >
                            {deleteCustomerMutation.isPending ? 'Deleting...' : 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Settings Edit Dialog */}
            <Dialog open={isSettingsEditDialogOpen} onOpenChange={setIsSettingsEditDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Edit Settings</DialogTitle>
                    </DialogHeader>
                    {customer.settings && (
                        <div className="space-y-4">
                            <p className="text-sm text-muted-foreground">
                                Edit the settings for this customer.
                            </p>
                            <pre className="overflow-auto max-h-[400px] bg-muted p-3 rounded text-xs border">
                                {JSON.stringify(customer.settings, null, 2)}
                            </pre>
                        </div>
                    )}
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setIsSettingsEditDialogOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button>Save Changes</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </PageContainer>
    );
};

export default CustomerDetailsPage;
